import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import KeywordCreateDialog, { KEYWORD_CATEGORY_OPTIONS } from "@/components/keywords/KeywordCreateDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { trpc } from "@/lib/trpc";
import { Plus, RefreshCw, Trash2, Edit, TrendingUp, TrendingDown, ArrowUpDown, CheckCircle2, XCircle, Loader2, Bot } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const LLM_MANUAL_BATCH_LOG_ID_KEY = "llm_manual_batch_log_id";

export default function Keywords() {
  // Auth gating is handled by DashboardLayout; avoid hard redirects that can cause login loops.
  const { loading: authLoading } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<number[]>([]);
  const [editingKeyword, setEditingKeyword] = useState<any>(null);
  const [currentUpdatingIndex, setCurrentUpdatingIndex] = useState(0);
  const [llmDetailKeyword, setLlmDetailKeyword] = useState<any>(null);
  const [isLlmDetailOpen, setIsLlmDetailOpen] = useState(false);
  const [isBatchLlmChecking, setIsBatchLlmChecking] = useState(false);
  const [batchLlmProgress, setBatchLlmProgress] = useState({ done: 0, total: 0 });
  const [llmCheckingKeywordId, setLlmCheckingKeywordId] = useState<number | null>(null);
  const [batchLlmFailed, setBatchLlmFailed] = useState(0);
  const [suppressLlmToasts, setSuppressLlmToasts] = useState(false);
  const [llmBatchLogId, setLlmBatchLogId] = useState<number | null>(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Batch update state
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  // Form states
  const [newKeyword, setNewKeyword] = useState("");
  const [newCategory, setNewCategory] = useState<string>("未分類");

  const utils = trpc.useUtils();

  const keywordQueryInput = selectedCategory ? { category: selectedCategory } : undefined;
  const { data: keywords = [], isLoading, isFetching, refetch: refetchKeywords } = trpc.keywords.getAll.useQuery(
    keywordQueryInput
  );
  const { data: checkHistory = [], isLoading: isCheckHistoryLoading } = trpc.llmCitation.getCheckHistory.useQuery(
    { keywordId: llmDetailKeyword?.id ?? 0, limit: 80 },
    { enabled: isLlmDetailOpen && Boolean(llmDetailKeyword?.id) }
  );
  const getLlmTotalChecks = (k: any) => Number(k?.llmTotalChecks ?? k?.llm_total_checks ?? 0);
  const getLlmGeminiLastMention = (k: any) => k?.llmGeminiLastMention ?? k?.llm_gemini_last_mention ?? null;
  const getLlmChatgptLastMention = (k: any) => k?.llmChatgptLastMention ?? k?.llm_chatgpt_last_mention ?? null;
  const getLlmGeminiMentionCount = (k: any) => Number(k?.llmGeminiMentionCount ?? k?.llm_gemini_mention_count ?? 0);
  const getLlmChatgptMentionCount = (k: any) => Number(k?.llmChatgptMentionCount ?? k?.llm_chatgpt_mention_count ?? 0);
  const getAioLastCheckedAt = (k: any) => k?.aioLastCheckedAt ?? k?.aio_last_checked_at ?? null;
  const getAioHasData = (k: any) => {
    const value = k?.aioHasData ?? k?.aio_has_data ?? null;
    return typeof value === "boolean" ? value : false;
  };
  const getAioCheckStatus = (k: any) =>
    k?.aioCheckStatus ?? k?.aio_check_status ?? (getAioLastCheckedAt(k) ? (getAioHasData(k) ? "has_overview" : "no_overview") : "unchecked");
  const getAioBrandMentioned = (k: any) => {
    const value = k?.aioBrandMentioned ?? k?.aio_brand_mentioned ?? null;
    return typeof value === "boolean" ? value : null;
  };

  const updatingKeywords = keywords.filter((k: any) => selectedKeywords.includes(k.id));

  const { data: categories = [] } = trpc.keywords.getCategories.useQuery();

  const updateMutation = trpc.keywords.update.useMutation({
    onSuccess: () => {
      utils.keywords.getAll.invalidate();
      setIsEditDialogOpen(false);
      setEditingKeyword(null);
      toast.success("關鍵字更新成功");
    },
    onError: (error) => {
      toast.error(`更新失敗：${error.message}`);
    },
  });

  const deleteMutation = trpc.keywords.delete.useMutation({
    onSuccess: () => {
      utils.keywords.getAll.invalidate();
      utils.keywords.getCategories.invalidate();
      toast.success("關鍵字刪除成功");
    },
    onError: (error) => {
      toast.error(`刪除失敗：${error.message}`);
    },
  });

  const checkLlmMutation = trpc.llmCitation.checkKeyword.useMutation({
    onSuccess: (data) => {
      utils.keywords.getAll.invalidate();
      refetchKeywords();
      if (!suppressLlmToasts) {
        const aioHasOverview = Boolean((data as any)?.aio?.hasOverview);
        const aioMentioned = (data as any)?.aio?.brandMentioned === true;
        const aioStatus = aioHasOverview
          ? (aioMentioned ? "有摘要（有提及）" : "有摘要（未提及）")
          : "無摘要";
        const geminiStatus = data.gemini.mentioned ? "有引用" : "無引用";
        const chatgptStatus = data.chatgpt.mentioned ? "有引用" : "無引用";
        toast.success(`AI 檢查完成\nAIO: ${aioStatus}\nGemini: ${geminiStatus}\nChatGPT: ${chatgptStatus}`);
      }
    },
    onError: () => {
      refetchKeywords();
      if (!suppressLlmToasts) {
        toast.error("AI 檢查未完成，請稍後重試");
      }
    },
  });

  const batchCheckQueuedMutation = trpc.llmCitation.batchCheckQueued.useMutation();
  const batchRunLogQuery = trpc.llmCitation.getBatchRunLog.useQuery(
    { logId: llmBatchLogId ?? 0 },
    {
      enabled: llmBatchLogId != null,
      refetchInterval: 2000,
      refetchIntervalInBackground: true,
    }
  );

  useEffect(() => {
    // Restore in-progress batch progress across refresh/navigation.
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem(LLM_MANUAL_BATCH_LOG_ID_KEY);
    if (!raw) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setLlmBatchLogId(parsed);
    setIsBatchLlmChecking(true);
  }, []);

  useEffect(() => {
    if (!batchRunLogQuery.data || llmBatchLogId == null) return;
    const log: any = batchRunLogQuery.data;

    const total = Number(log.totalItems ?? 0);
    const done = Number(log.processedItems ?? 0);
    const failed = Number(log.failureCount ?? 0);

    let currentKeywordId: number | null = null;
    try {
      const payload = log.logs ? JSON.parse(log.logs) : {};
      const v = payload?.currentKeywordId;
      if (typeof v === "number") currentKeywordId = v;
      if (typeof v === "string" && v.trim()) currentKeywordId = Number(v);
      if (currentKeywordId != null && !Number.isFinite(currentKeywordId)) currentKeywordId = null;
    } catch {
      currentKeywordId = null;
    }

    setIsBatchLlmChecking(log.status === "running");
    setBatchLlmProgress({ done, total });
    setBatchLlmFailed(failed);
    setLlmCheckingKeywordId(currentKeywordId);

    const isDone = log.status !== "running" || (total > 0 && done >= total);
    if (isDone) {
      setIsBatchLlmChecking(false);
      setBatchLlmProgress({ done: 0, total: 0 });
      setBatchLlmFailed(0);
      setLlmCheckingKeywordId(null);
      setLlmBatchLogId(null);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(LLM_MANUAL_BATCH_LOG_ID_KEY);
      }
      utils.keywords.getAll.invalidate();
      toast.success(`AI 批次檢查完成：${done} 筆已更新`);
    }
  }, [batchRunLogQuery.data, llmBatchLogId, utils.keywords.getAll]);

  const batchUpdateMutation = trpc.keywords.batchUpdate.useMutation({
    // We handle success/error manually in the loop
  });

  useEffect(() => {
    if (!isBatchUpdating || updatingKeywords.length === 0) {
      setCurrentUpdatingIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentUpdatingIndex(prev => (prev + 1) % updatingKeywords.length);
    }, 1200);

    return () => clearInterval(interval);
  }, [isBatchUpdating, updatingKeywords.length]);

  const resetForm = () => {
    setNewKeyword("");
    setNewCategory("未分類");
  };

  const formatDateTime = (value: any) => {
    if (!value) return "-";
    return new Date(value).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleEdit = (keyword: any) => {
    setEditingKeyword(keyword);
    setNewKeyword(keyword.keyword);
    const category = keyword.category || "未分類";
    setNewCategory(KEYWORD_CATEGORY_OPTIONS.includes(category as any) ? category : "未分類");
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!editingKeyword) return;

    updateMutation.mutate({
      id: editingKeyword.id,
      keyword: newKeyword.trim() || undefined,
      category: newCategory,
    });
  };

  const handleDelete = (id: number) => {
    if (confirm("確定要刪除這個關鍵字嗎？")) {
      deleteMutation.mutate({ id });
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedKeywords.length === 0) {
      toast.error("請至少選擇一個關鍵字");
      return;
    }

    if (!confirm(`確定要更新選中的 ${selectedKeywords.length} 個關鍵字的趨勢數據嗎？這可能需要一些時間。`)) {
      return;
    }

    setIsBatchUpdating(true);
    setBatchProgress({ current: 0, total: selectedKeywords.length });

    // Chunk size of 3 to avoid timeouts (2s delay per keyword = 6s per chunk)
    // Cloudflare Workers usually have 30s limit, so 3 * 2s = 6s is safe
    const CHUNK_SIZE = 3;
    const chunks = [];
    for (let i = 0; i < selectedKeywords.length; i += CHUNK_SIZE) {
      chunks.push(selectedKeywords.slice(i, i + CHUNK_SIZE));
    }

    let successCount = 0;
    let errorCount = 0;

    for (const chunk of chunks) {
      try {
        const result = await batchUpdateMutation.mutateAsync({ ids: chunk });
        const updated = Number((result as any)?.updatedCount ?? 0);
        const skipped = Number((result as any)?.skippedCount ?? 0);
        successCount += updated;
        errorCount += skipped;
      } catch (error: any) {
        console.error("Batch chunk failed:", error);
        errorCount += chunk.length;
        const errMsg = error?.message || "批次更新失敗";
        toast.error(errMsg);
      } finally {
        setBatchProgress(prev => ({ ...prev, current: Math.min(prev.current + chunk.length, selectedKeywords.length) }));
      }
    }

    setIsBatchUpdating(false);
    utils.keywords.getAll.invalidate();
    setSelectedKeywords([]);

    if (errorCount > 0) {
      toast.warning(`更新完成：${successCount} 個成功，${errorCount} 個失敗`);
    } else {
      toast.success(`成功更新 ${successCount} 個關鍵字`);
    }
  };

  const toggleKeywordSelection = (id: number) => {
    setSelectedKeywords(prev =>
      prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedKeywords.length === keywords.length) {
      setSelectedKeywords([]);
    } else {
      setSelectedKeywords(keywords.map((k: any) => k.id));
    }
  };

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'desc' }; // Default to desc for numbers like growth rate
    });
  };

  const handleLlmCheck = async (keywordId: number) => {
    if (isBatchLlmChecking) return;
    setLlmCheckingKeywordId(keywordId);
    try {
      await checkLlmMutation.mutateAsync({ keywordId });
    } finally {
      setLlmCheckingKeywordId(null);
    }
  };

  const handleBatchLlmCheck = async () => {
    if (selectedKeywords.length === 0) {
      toast.warning('請先選擇要檢查的關鍵字');
      return;
    }

    if (!confirm(`確定要檢查選中的 ${selectedKeywords.length} 個關鍵字嗎？檢查期間請勿關閉頁面。`)) {
      return;
    }

    // Oldest-first approximation using existing fields:
    // 1) Never checked (llmTotalChecks=0) first
    // 2) Then by last mention time (older first); if never mentioned, treat as very old
    const selectedKeywordObjs = keywords.filter((k: any) => selectedKeywords.includes(k.id));
    const ordered = [...selectedKeywordObjs].sort((a: any, b: any) => {
      const aNever = getLlmTotalChecks(a) <= 0;
      const bNever = getLlmTotalChecks(b) <= 0;
      if (aNever !== bNever) return aNever ? -1 : 1;

      const aLast = Math.max(
        getLlmGeminiLastMention(a) ? new Date(getLlmGeminiLastMention(a)).getTime() : 0,
        getLlmChatgptLastMention(a) ? new Date(getLlmChatgptLastMention(a)).getTime() : 0
      );
      const bLast = Math.max(
        getLlmGeminiLastMention(b) ? new Date(getLlmGeminiLastMention(b)).getTime() : 0,
        getLlmChatgptLastMention(b) ? new Date(getLlmChatgptLastMention(b)).getTime() : 0
      );

      return aLast - bLast;
    });

    setSuppressLlmToasts(true);
    try {
      const res = await batchCheckQueuedMutation.mutateAsync({
        keywordIds: ordered.map((k: any) => k.id),
      });
      const newLogId = Number((res as any)?.logId);
      if (!Number.isFinite(newLogId) || newLogId <= 0) {
        throw new Error("Missing logId");
      }
      setSelectedKeywords([]);
      setLlmBatchLogId(newLogId);
      setIsBatchLlmChecking(true);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LLM_MANUAL_BATCH_LOG_ID_KEY, String(newLogId));
      }
      setBatchLlmProgress({ done: 0, total: ordered.length });
      setBatchLlmFailed(0);
    } catch (_e: any) {
      toast.error("AI 批次檢查啟動失敗，請重試");
    } finally {
      setSuppressLlmToasts(false);
    }
  };

  const handleViewLlmDetail = (keyword: any) => {
    setLlmDetailKeyword(keyword);
    setIsLlmDetailOpen(true);
  };

  const sortedKeywords = [...keywords].sort((a: any, b: any) => {
    if (!sortConfig) return 0;
    const { key, direction } = sortConfig;

    let aValue = a[key];
    let bValue = b[key];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = -Infinity;
    if (bValue === null || bValue === undefined) bValue = -Infinity;

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  if (authLoading || (isLoading && keywords.length === 0)) {
    // NOTE: keep the page stable during background refetches.
    // We only block-render on initial auth load or initial query load.
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="animate-spin h-8 w-8 text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">關鍵字管理</h1>
            <p className="text-muted-foreground mt-2">
              管理追蹤的關鍵字，並自動更新趨勢數據
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              onClick={handleBatchUpdate}
              disabled={selectedKeywords.length === 0 || isBatchUpdating}
            >
              {isBatchUpdating ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {isBatchUpdating
                ? `更新中 (${batchProgress.current}/${batchProgress.total})`
                : "更新選中項目"}
            </Button>
            <Button
              variant="outline"
              onClick={handleBatchLlmCheck}
              disabled={selectedKeywords.length === 0 || isBatchLlmChecking || authLoading}
              className="text-purple-600 hover:text-purple-700"
            >
              {isBatchLlmChecking ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Bot className="mr-2 h-4 w-4" />
              )}
              {isBatchLlmChecking
                ? `AI 檢查中 (${batchLlmProgress.done}/${batchLlmProgress.total})`
                : "AI 檢查選中項目"}
            </Button>
            {isBatchUpdating && (
              <div className="ml-4 text-sm text-muted-foreground">
                正在分批更新... 請勿關閉頁面
              </div>
            )}
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            新增關鍵字
          </Button>
        </div>

        {isBatchLlmChecking && batchLlmProgress.total > 0 && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm">
                  <div className="font-medium">AI 批次檢查進行中</div>
                  <div className="text-muted-foreground">
                    進度：{batchLlmProgress.done}/{batchLlmProgress.total}
                    {llmCheckingKeywordId != null ? `（正在檢查 #${llmCheckingKeywordId}）` : ""}
                    {batchLlmFailed > 0 ? `（失敗 ${batchLlmFailed}）` : ""}
                    {isFetching ? "（更新中...）" : ""}
                  </div>
                </div>
                <div className="w-[260px] max-w-full">
                  <Progress
                    value={(batchLlmProgress.done / Math.max(1, batchLlmProgress.total)) * 100}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>關鍵字列表</CardTitle>
                <CardDescription>
                  管理您的關鍵字追蹤列表
                  <span className="ml-4 text-xs">
                    關鍵字數量: {keywords.length}
                  </span>
                </CardDescription>
              </div>
              <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? undefined : v)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="選擇分類" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部分類</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={keywords.length > 0 && selectedKeywords.length === keywords.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>關鍵字</TableHead>
                  <TableHead>分類</TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleSort('growthRateWoW')}
                  >
                    <div className="flex items-center justify-end gap-1">
                      WoW
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead className="text-center">AIO</TableHead>
                  <TableHead className="text-center">Gemini</TableHead>
                  <TableHead className="text-center">ChatGPT</TableHead>
                  <TableHead className="text-right">最近更新</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedKeywords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      尚無關鍵字資料，請新增關鍵字開始追蹤
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedKeywords.map((keyword: any) => (
                    <TableRow key={keyword.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedKeywords.includes(keyword.id)}
                          onCheckedChange={() => toggleKeywordSelection(keyword.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{keyword.keyword}</TableCell>
                      <TableCell>
                        {keyword.category && (
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                            {keyword.category}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {keyword.growthRateWoW !== null && keyword.growthRateWoW !== undefined ? (
                          <span className={`inline-flex items-center text-xs ${keyword.growthRateWoW >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {keyword.growthRateWoW >= 0 ? (
                              <TrendingUp className="mr-1 h-3 w-3" />
                            ) : (
                              <TrendingDown className="mr-1 h-3 w-3" />
                            )}
                            {keyword.growthRateWoW > 0 ? "+" : ""}{keyword.growthRateWoW.toFixed(2)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">無數據</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getAioCheckStatus(keyword) === "unchecked" ? (
                            <span className="text-xs text-muted-foreground">未檢查</span>
                          ) : getAioCheckStatus(keyword) === "failed" ? (
                            <>
                              <XCircle className="h-4 w-4 text-red-500" />
                              <span className="text-xs text-muted-foreground">檢查失敗</span>
                            </>
                          ) : getAioCheckStatus(keyword) === "no_overview" ? (
                            <>
                              <XCircle className="h-4 w-4 text-gray-500" />
                              <span className="text-xs text-gray-600">無摘要</span>
                            </>
                          ) : getAioBrandMentioned(keyword) === true ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-muted-foreground">
                                {new Date(getAioLastCheckedAt(keyword)).toLocaleString('zh-TW', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                })}
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-red-400" />
                              <span className="text-xs text-muted-foreground">未提及</span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getLlmGeminiLastMention(keyword) ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-muted-foreground">
                                {new Date(getLlmGeminiLastMention(keyword)).toLocaleString('zh-TW', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                })}
                              </span>
                            </>
                          ) : getLlmTotalChecks(keyword) > 0 ? (
                            <>
                              <XCircle className="h-4 w-4 text-red-400" />
                              <span className="text-xs text-muted-foreground">未提及</span>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">未檢查</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getLlmChatgptLastMention(keyword) ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-muted-foreground">
                                {new Date(getLlmChatgptLastMention(keyword)).toLocaleString('zh-TW', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                })}
                              </span>
                            </>
                          ) : getLlmTotalChecks(keyword) > 0 ? (
                            <>
                              <XCircle className="h-4 w-4 text-red-400" />
                              <span className="text-xs text-muted-foreground">未提及</span>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">未檢查</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {keyword.updatedAt ? new Date(keyword.updatedAt).toLocaleString('zh-TW', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        }) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLlmCheck(keyword.id)}
                            disabled={isBatchLlmChecking || llmCheckingKeywordId === keyword.id}
                            className="h-8 px-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            title="AI 檢查"
                          >
                            {llmCheckingKeywordId === keyword.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Bot className="h-4 w-4" />
                            )}
                          </Button>
                          {getLlmTotalChecks(keyword) > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewLlmDetail(keyword)}
                              className="h-8 px-2"
                              title="查看詳情"
                            >
                              <span className="text-xs">詳情</span>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(keyword)}
                            className="h-8 px-2"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            <span className="text-xs">編輯</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(keyword.id)}
                            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            <span className="text-xs">刪除</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <KeywordCreateDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          defaultKeyword={newKeyword}
          defaultCategory={newCategory}
          onCreated={() => {
            resetForm();
          }}
        />

        {/* 編輯關鍵字對話框 */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>編輯關鍵字</DialogTitle>
              <DialogDescription>
                修改關鍵字資訊
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-keyword">關鍵字 *</Label>
                <Input
                  id="edit-keyword"
                  placeholder="例如：植牙推薦"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">分類</Label>
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger id="edit-category">
                    <SelectValue placeholder="選擇分類" />
                  </SelectTrigger>
                  <SelectContent>
                    {KEYWORD_CATEGORY_OPTIONS.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "更新中..." : "更新"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* LLM 引用詳情對話框 */}
        <Dialog open={isLlmDetailOpen} onOpenChange={setIsLlmDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white border-slate-200 shadow-2xl">
            <DialogHeader>
              <DialogTitle>LLM 引用詳情 - {llmDetailKeyword?.keyword}</DialogTitle>
              <DialogDescription>
                查看關鍵字在 LLM 中的品牌提及情況
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* 統計概覽 */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      總檢查次數
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {getLlmTotalChecks(llmDetailKeyword)}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Gemini 引用率
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {getLlmTotalChecks(llmDetailKeyword) > 0
                        ? Math.round((getLlmGeminiMentionCount(llmDetailKeyword) / getLlmTotalChecks(llmDetailKeyword)) * 100)
                        : 0}%
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getLlmGeminiMentionCount(llmDetailKeyword)} 次提及
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      ChatGPT 引用率
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      {getLlmTotalChecks(llmDetailKeyword) > 0
                        ? Math.round((getLlmChatgptMentionCount(llmDetailKeyword) / getLlmTotalChecks(llmDetailKeyword)) * 100)
                        : 0}%
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getLlmChatgptMentionCount(llmDetailKeyword)} 次提及
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* 最近提及時間 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gemini 最近提及</Label>
                  <div className="text-sm">
                    {getLlmGeminiLastMention(llmDetailKeyword) ? (
                      <span className="text-green-600 font-medium">
                        {new Date(getLlmGeminiLastMention(llmDetailKeyword)).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">從未提及</span>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ChatGPT 最近提及</Label>
                  <div className="text-sm">
                    {getLlmChatgptLastMention(llmDetailKeyword) ? (
                      <span className="text-purple-600 font-medium">
                        {new Date(getLlmChatgptLastMention(llmDetailKeyword)).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">從未提及</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>檢查歷史紀錄（AIO / Gemini / ChatGPT）</Label>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>時間</TableHead>
                        <TableHead>來源</TableHead>
                        <TableHead>狀態</TableHead>
                        <TableHead className="text-center">Confidence</TableHead>
                        <TableHead className="text-center">AIO</TableHead>
                        <TableHead className="text-center">Gemini</TableHead>
                        <TableHead className="text-center">ChatGPT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isCheckHistoryLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            載入中...
                          </TableCell>
                        </TableRow>
                      ) : checkHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            尚無檢查紀錄
                          </TableCell>
                        </TableRow>
                      ) : (
                        checkHistory.map((row: any, idx: number) => (
                          <TableRow key={`${row.provider || row.source}-${row.checkedAt}-${idx}`}>
                            <TableCell>{formatDateTime(row.checkedAt)}</TableCell>
                            <TableCell>{row.provider ? String(row.provider).toUpperCase() : (row.source === "aio" ? "AIO" : "LLM")}</TableCell>
                            <TableCell>{row.status || "-"}</TableCell>
                            <TableCell className="text-center">{row.confidenceScore ?? "-"}</TableCell>
                            <TableCell className="text-center">
                              {row.aio ? (
                                row.aio.hasOverview
                                  ? (row.aio.brandMentioned ? "有摘要・有提及" : "有摘要・未提及")
                                  : "無摘要"
                              ) : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              {row.gemini ? (row.gemini.linked ? "有連結" : row.gemini.mentioned ? "有提及" : "未提及") : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              {row.chatgpt ? (row.chatgpt.linked ? "有連結" : row.chatgpt.mentioned ? "有提及" : "未提及") : "-"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* 提示信息 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>說明：</strong>每次檢查會查詢 LLM 3 次，只要有 1 次提及品牌即算「有引用」。
                  引用率 = 有引用的檢查次數 / 總檢查次數。
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsLlmDetailOpen(false)}>
                關閉
              </Button>
              <Button onClick={() => handleLlmCheck(llmDetailKeyword?.id)}>
                <Bot className="mr-2 h-4 w-4" />
                立即檢查
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout >
  );
}
